import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Barn, CaretUpDown, Check, House, Spinner, X } from '@phosphor-icons/react';
import classNames from 'classnames';
import { Contact, Role } from 'openapi';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarIcon, AvatarInitials, AvatarSize } from '../../ui/Avatar';
import { contactInitials, contactName, ContactType, getContactType, textFilter } from '../../utilities/Contact';
import { useSearchParams } from 'react-router-dom';
import { SpinnerSize } from 'ui/Loading/Spinner';

interface Props {
  loading?: boolean;
  contacts: Contact[];
  // Set this value if we want the user to select a user with a role.
  // It divides the list into two groups (role, and others).
  pickRole?: Role;
  onSearch: (contact: Contact) => void;
  onClear: () => void;
  placeholderText: string;
  className?: string;
  selectedContact?: Contact;
}

// A component that is shown on top of a page for searching contacts and other strings.
export default function SearchContact({
  onSearch,
  onClear,
  contacts,
  pickRole,
  placeholderText,
  className,
  loading,
  selectedContact,
}: Props): JSX.Element {
  const [findContactQuery, setFindContactQuery] = useState<string>('');
  // flag that indicates if we have consumed the query params
  // if consumed, we don't need to check the query params again
  const [consumedQueryParams, setConsumedQueryParams] = useState(false);
  // the current value holds the parsed value of the findContactQuery variable
  // this is either a string or a contact and set as the value of the combobox
  const [currentValue, setCurrentValue] = useState<string | Contact>(selectedContact ?? '');

  const inputRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const inputElementRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  // Return a contact name in a human readable way. If the contact doesn't have
  // a name, then we return 'Nameless contact' as text.
  const nameContact = useCallback(
    (contact: Contact): string => {
      return contactName(contact) ?? `<${t('nameless-contact', 'Nameless contact')}>`;
    },
    [t],
  );

  // Return a list of contacts filtered by the findContactQuery text.
  const filteredContacts = useMemo((): Contact[] => {
    return textFilter(contacts ?? [], findContactQuery);
  }, [contacts, findContactQuery]);

  /**
   * Format the list for the virtual scrolling.
   */
  const virtualScrolling = useMemo((): (string | Contact)[] => {
    const options: (string | Contact)[] = [];

    if (pickRole) {
      options.push(`heading-${pickRole.name ?? ''}`);
      const roleContacts = filteredContacts.filter(contact => contact.roles?.includes(pickRole.uid));
      if (roleContacts.length === 0) {
        options.push('no-contacts-for-role');
      }
      for (const item of roleContacts) {
        options.push(item);
      }
      options.push(`heading-${t('others', 'Others')}`);
      const others = filteredContacts.filter(contact => !contact.roles?.includes(pickRole.uid));
      if (others.length === 0) {
        options.push('no-contacts-for-role');
      }
      for (const item of others) {
        options.push(item);
      }
    } else {
      for (const item of filteredContacts) {
        options.push(item);
      }
    }

    if (loading) {
      options.push('loading');
    }

    if (filteredContacts.length === 0) {
      options.push('no-result');
    }

    return options;
  }, [filteredContacts, loading, pickRole, t]);

  /**
   * Detect if we have a search_contact_uid or search in the URL and set the current value
   * based on that.
   */
  useEffect(() => {
    // we only want to consume the query params once
    if (!consumedQueryParams) {
      const contactUid = searchParams.get('search_contact_uid');

      // in case we deal with a contactUid, we need to find the contact in the list of contacts
      const contact = contactUid && contacts.find(contact => contact.uid === contactUid);

      if (contact) {
        setCurrentValue(contact);
        onSearch(contact);
      }

      // mark as consumed
      setConsumedQueryParams(true);
    }

    if (selectedContact) {
      setCurrentValue(selectedContact);
    }
  }, [contacts, searchParams, selectedContact]); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Combobox<string | Contact>
      virtual={{ options: virtualScrolling }}
      value={currentValue}
      immediate={true} // show the options when the input is focused
      onChange={searchFor => {
        if (!searchFor) {
          onClear();
        } else {
          onSearch(searchFor as Contact);
          setCurrentValue(searchFor as Contact);
        }
      }}
    >
      {({ open }) => (
        <div className={classNames('relative grow', className)}>
          <div
            ref={inputRef}
            className={classNames('flex relative w-full cursor-default overflow-hidden rounded-lg border bg-white focus:outline-none', {
              'border-primary': open,
            })}
          >
            <ComboboxInput
              ref={inputElementRef}
              placeholder={placeholderText}
              className='placeholder:italic placeholder:text-sm h-9 w-full border-none p-2 pr-10 text-sm leading-5 text-gray-900 focus:outline-none focus:ring-0'
              displayValue={searchFor => contactName(searchFor as Contact) ?? String(searchFor)}
              onChange={event => {
                setFindContactQuery(event.target.value);
                if (!event.target.value) {
                  onClear();
                }
              }}
            />
            {(findContactQuery !== '' || inputElementRef.current?.value !== '') && (
              <button
                type='button'
                className='absolute inset-y-0 right-9 flex items-center bg-gray-50 border p-1 rounded-full m-1.5 w-6 h-6'
                onClick={() => {
                  if (inputElementRef.current) {
                    inputElementRef.current.value = '';
                    setFindContactQuery('');
                    onClear();
                  }
                }}
              >
                <X size={25} />
              </button>
            )}

            <ComboboxButton className='absolute inset-y-0 right-0 flex items-center pr-3 pl-1' onClick={e => e.stopPropagation()}>
              <CaretUpDown className='h-5 w-5 text-gray-600' aria-hidden='true' />
            </ComboboxButton>
          </div>

          <ComboboxOptions
            anchor='bottom'
            // NOTE: we could use var(--input-width), but that is not fully working as it is not fully set to the full width of the input
            // there for we use the inputRef to get the width of the input
            // SEE: https://headlessui.com/react/combobox#setting-the-dropdown-width
            style={{ width: inputRef.current?.offsetWidth }}
            // NOTE: --anchor-gap is a magic CSS/Tailwind variable that are coming from headlessUI
            // NOTE: it seems BORDER is required to make the list visible
            className='border !max-h-60 [--anchor-gap:4px] overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[999]'
          >
            {({ option }: { option: string | Contact }) => {
              const isDefaultOption = typeof option !== 'string';
              const isLoading = option === 'loading';
              const noResult = option === 'no-result';
              const noResultForRole = option === 'no-contacts-for-role';
              const isHeading = typeof option === 'string' && option.startsWith('heading-');
              const headingText: string | undefined = typeof option === 'string' ? option.substring(8) : undefined;

              return (
                <ComboboxOption
                  className={classNames('relative cursor-pointer py-2 px-3 w-full', {
                    'data-[focus]:bg-gray-200 data-[focus]:text-gray-800 text-gray-900': isDefaultOption,
                  })}
                  value={isDefaultOption ? option : undefined}
                >
                  {({ selected }) => (
                    <>
                      {isDefaultOption && (
                        <div className='flex gap-2 items-center'>
                          {selected && (
                            <span className='absolute inset-y-0 left-2 flex items-center text-blue-600'>
                              <Check size={16} aria-hidden='true' />
                            </span>
                          )}
                          <div className='flex gap-2 items-center'>
                            {[ContactType.Business].includes(getContactType(option)) && (
                              <AvatarIcon size={AvatarSize.XSmall} icon={<House />} uuid={option.uid} />
                            )}
                            {[ContactType.Stable].includes(getContactType(option)) && (
                              <AvatarIcon size={AvatarSize.XSmall} icon={<Barn />} uuid={option.uid} />
                            )}
                            {[ContactType.User, ContactType.Contact].includes(getContactType(option)) && (
                              <AvatarInitials
                                size={AvatarSize.XSmall}
                                initials={contactInitials(option)}
                                uuid={option.uid}
                                color={option.color}
                              />
                            )}
                            <span className={`block text-sm truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {nameContact(option)}
                            </span>
                          </div>
                        </div>
                      )}

                      {isHeading && (
                        <div className='cursor-default select-none py-1.5 text-gray-700 text-sm items-center font-bold'>{headingText}</div>
                      )}

                      {noResultForRole && (
                        <div className='cursor-default select-none italic text-gray-600 text-sm'>
                          {t('no-options-available', 'No options available')}
                        </div>
                      )}

                      {isLoading && (
                        <div className='relative cursor-default select-none py-2 px-4 text-gray-700 text-sm flex gap-x-1 items-center'>
                          <Spinner size={SpinnerSize.XSmall} />
                          {t('loading', 'Loading')}...
                        </div>
                      )}

                      {noResult && (
                        <div className='relative cursor-default select-none py-2 px-3 italic text-gray-600'>
                          <span>{t('nothing-found', 'Nothing found')}</span>
                        </div>
                      )}
                    </>
                  )}
                </ComboboxOption>
              );
            }}
          </ComboboxOptions>
        </div>
      )}
    </Combobox>
  );
}
